import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import TextInput from './TextInput.jsx';
import GenericSelect from './GenericSelect';
import CheckboxInput from './CheckboxInput';
import Button from './Button';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Label = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ItemCard = styled.div`
  border: 1px solid #e8ecf0;
  border-radius: ${({ theme }) => theme.metrics.radius.medium};
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #fafbfc;
`;

const ItemHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ItemIndex = styled.span`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const RemoveBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: none;
  color: #d9534f;
  cursor: pointer;
  &:hover { background: #fdecea; }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const CheckRow = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
`;

const ErrorText = styled.div`
  font-size: 12px;
  color: #d9534f;
`;

// Editor for Pb/ObTaskTemplate.ItemsJson entries. Value is the PARSED array:
//   [{ key, title, ownerRole, slaHours, isMandatory, dependsOn[],
//      requiresHrApproval | requiresManagerApproval, period?, documentTypes[] }]
// Keys are generated once at row-add time and never change afterwards — other
// items' dependsOn lists reference them, and materialized Pb/ObTask rows resolve
// dependencies by these keys at runtime.
// documentTypeOptions doluysa her görev kartında "Bağlı Belgeler" çoklu seçimi
// görünür (yalnız işe hazırlık şablonu geçer): seçilen belge türleri süreç
// açılışında otomatik belge gereksinimi yaratır ve bu belgeler yüklenmeden
// görev tamamlanamaz.
//
// ═══ OBF-03 — İKİ MODÜL, İKİ SÖZLEŞME ═══
// Bu düzenleyici PB için yazılmıştı ve OB şablon sayfası onu OLDUĞU GİBİ
// kullanıyordu. Sonuç: OB materializer'ının OKUDUĞU iki alanı ÜRETEMİYORDU —
// `period` (ObOnboardingDataAccess: `?? "day_1_30"`) ve `requiresManagerApproval`
// (`false`). Yani webden yazılan HER işe alıştırma görevi ilk 30 güne düşüyor,
// 60/90. gün onay ekranları BOŞ checklist üzerine yeşil tik basıyor ve görev
// yönetici onayı dalı hiç çalışmıyordu.
//   • periodOptions dolu → dönem seçimi görünür ve yeni kalemlere yazılır (OB).
//   • approvalField → onay bayrağının ADI; her modül KENDİ okuduğu adı geçer
//     (PB: requiresHrApproval, OB: requiresManagerApproval).
export default function TaskTemplateItemsEditor({
  label, value, onChange, ownerRoles = [], documentTypeOptions = [],
  periodOptions = [], approvalField = 'requiresHrApproval', error,
}) {
  const { t } = useTranslation();
  const items = Array.isArray(value) ? value : [];

  const periodSelectOptions = periodOptions.map((p) => ({
    id: p,
    name: t(`taskTemplateItems.period.${p}`, { defaultValue: p }),
  }));

  const ownerRoleOptions = ownerRoles.map((r) => ({
    id: r,
    name: t(`preboardingTask.ownerRole.${r}`, { defaultValue: r }),
  }));

  const nextKey = () => {
    const used = new Set(items.map((it) => it.key));
    let n = items.length + 1;
    while (used.has(`task-${n}`)) n += 1;
    return `task-${n}`;
  };

  const updateItem = (index, patch) => {
    const next = items.map((it, i) => (i === index ? { ...it, ...patch } : it));
    onChange(next);
  };

  const removeItem = (index) => {
    const removedKey = items[index]?.key;
    const next = items
      .filter((_, i) => i !== index)
      .map((it) => ({
        ...it,
        dependsOn: (it.dependsOn || []).filter((k) => k !== removedKey),
      }));
    onChange(next);
  };

  const addItem = () => {
    const seed = {
      key: nextKey(),
      title: '',
      ownerRole: 'hr',
      slaHours: 0,
      isMandatory: false,
      [approvalField]: false,
      dependsOn: [],
    };
    // Dönem kavramı olan modülde (OB) yeni kalem SESSİZCE ilk döneme düşmesin —
    // alan AÇIKÇA yazılır, kullanıcı görüp değiştirebilir (OBF-03).
    if (periodOptions.length > 0) seed.period = periodOptions[0];
    onChange([...items, seed]);
  };

  const dependsLabel = (key) => {
    const target = items.find((it) => it.key === key);
    return target?.title || key;
  };

  return (
    <Wrapper>
      {label && <Label>{label}</Label>}
      {items.map((item, index) => (
        <ItemCard key={item.key}>
          <ItemHeader>
            <ItemIndex>{t('taskTemplateItems.itemIndex', { index: index + 1 })}</ItemIndex>
            <RemoveBtn type="button" onClick={() => removeItem(index)} aria-label={t('common.delete')}>
              <Trash2 size={16} />
            </RemoveBtn>
          </ItemHeader>

          <TextInput
            label={t('taskTemplateItems.title')}
            value={item.title || ''}
            onChange={(e) => updateItem(index, { title: e.target.value })}
            required
          />

          <Row>
            <GenericSelect
              label={t('taskTemplateItems.ownerRole')}
              options={ownerRoleOptions}
              value={item.ownerRole || 'hr'}
              onChange={(val) => updateItem(index, { ownerRole: val })}
            />
            <TextInput
              label={t('taskTemplateItems.slaHours')}
              type="number"
              min={0}
              value={item.slaHours ?? 0}
              onChange={(e) => updateItem(index, { slaHours: e.target.value })}
            />
          </Row>

          {periodSelectOptions.length > 0 && (
            <GenericSelect
              label={t('taskTemplateItems.periodLabel')}
              options={periodSelectOptions}
              value={item.period || periodOptions[0]}
              onChange={(val) => updateItem(index, { period: val })}
            />
          )}

          <CheckRow>
            <CheckboxInput
              label={t('taskTemplateItems.isMandatory')}
              checked={!!item.isMandatory}
              onChange={(val) => updateItem(index, { isMandatory: val })}
            />
            <CheckboxInput
              label={t(`taskTemplateItems.${approvalField}`)}
              checked={!!item[approvalField]}
              onChange={(val) => updateItem(index, { [approvalField]: val })}
            />
          </CheckRow>

          {items.length > 1 && (
            <FormControl variant="standard" fullWidth>
              <InputLabel>{t('taskTemplateItems.dependsOn')}</InputLabel>
              <Select
                multiple
                value={item.dependsOn || []}
                onChange={(e) => {
                  const v = e.target.value;
                  updateItem(index, { dependsOn: Array.isArray(v) ? v : [] });
                }}
                renderValue={(selected) => selected.map(dependsLabel).join(', ')}
              >
                {items
                  .filter((other) => other.key !== item.key)
                  .map((other) => (
                    <MenuItem key={other.key} value={other.key}>
                      {dependsLabel(other.key)}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          )}

          {documentTypeOptions.length > 0 && (
            <FormControl variant="standard" fullWidth>
              <InputLabel>{t('taskTemplateItems.documentTypes')}</InputLabel>
              <Select
                multiple
                value={item.documentTypes || []}
                onChange={(e) => {
                  const v = e.target.value;
                  updateItem(index, { documentTypes: Array.isArray(v) ? v : [] });
                }}
                renderValue={(selected) => selected
                  .map((dt) => documentTypeOptions.find((o) => o.id === dt)?.name || dt)
                  .join(', ')}
              >
                {documentTypeOptions.map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {o.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </ItemCard>
      ))}

      {error && <ErrorText>{error}</ErrorText>}

      <Button type="button" variant="outline" onClick={addItem}>
        + {t('taskTemplateItems.addItem')}
      </Button>
    </Wrapper>
  );
}
